import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LoadingState,
  ErrorState,
  EmptyState,
  AttentionBadge,
  AskAboutThis,
  beliefSentence,
  severity,
  verdict,
} from "@/components/research-hub";
import { useThesisHealthScan } from "@/hooks/useResearchHubAnalyst";
import { useHouseViewConsistency } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;
const HUB = "/partner/research-hub";

const EMPTY_COPY = "Our views are holding together and the market agrees so far.";

export default function Divergence() {
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="space-y-1">
        <h1 className="font-heading text-xl font-semibold text-foreground">Where we diverge</h1>
        <p className="font-body text-sm text-muted-foreground">
          The views the market is testing, and the places our own thinking pulls apart.
        </p>
      </div>

      <Tabs defaultValue="market">
        <TabsList>
          <TabsTrigger value="market">Vs the market</TabsTrigger>
          <TabsTrigger value="ourselves">Vs ourselves</TabsTrigger>
        </TabsList>

        <TabsContent value="market" className="mt-4">
          <VsTheMarket />
        </TabsContent>

        <TabsContent value="ourselves" className="mt-4">
          <VsOurselves />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------ Vs the market -- */
function VsTheMarket() {
  const { data, isLoading, error, refetch } = useThesisHealthScan();

  if (isLoading) return <LoadingState rows={3} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;

  const rows: Row[] = Array.isArray(data) ? data : [];

  const contradictions = rows.filter(
    (r) => (r?.health_status ?? "").toLowerCase() === "live_contradiction",
  );
  // "No live signal to test this" is a health_status value, not a separate boolean column — the scan
  // returns no `no_signal_coverage` field, so the old filter matched nothing.
  const noCoverage = rows.filter(
    (r) => (r?.health_status ?? "").toLowerCase() === "no_signal_coverage",
  );

  if (contradictions.length === 0 && noCoverage.length === 0) {
    return <EmptyState title={EMPTY_COPY} tone="good" />;
  }

  return (
    <div className="space-y-6">
      {/* The market is moving against these */}
      <section className="space-y-2">
        <h2 className="font-heading text-base font-medium text-foreground">
          The market is moving against these
        </h2>
        {contradictions.length === 0 ? (
          <EmptyState title="Nothing the market is actively pushing against today." tone="good" />
        ) : (
          <div className="space-y-2">
            {contradictions.map((r, i) => {
              const sev = severity(r.severity);
              const subject = subjectOf(r);
              return (
                <Card key={r.signal_key ?? i} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-body text-sm font-medium min-w-0">{subject}</div>
                    <AttentionBadge label={sev.label} tone={sev.tone} className="shrink-0" />
                  </div>
                  {r.summary && (
                    <p className="font-body text-sm text-muted-foreground">{r.summary}</p>
                  )}
                  <div className="flex justify-end">
                    <AskAboutThis
                      prefill={`Why is the market moving against our view that ${subject}?`}
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* No live signal to test these */}
      {noCoverage.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-heading text-base font-medium text-muted-foreground">
            We can't test these against any live signal
          </h2>
          <div className="space-y-2">
            {noCoverage.map((r, i) => {
              const subject = subjectOf(r);
              return (
                <Card
                  key={r.signal_key ?? `no-${i}`}
                  className="p-3 flex items-center justify-between gap-3 bg-muted/30"
                >
                  <div className="font-body text-sm text-muted-foreground min-w-0 truncate">
                    {subject}
                  </div>
                  <AskAboutThis
                    prefill={`What would it take to test our view that ${subject} against a live signal?`}
                    className="shrink-0"
                  />
                </Card>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// The health-scan row carries a ready-made subject; fall back to a belief sentence
// built from any from/to/direction fields, never an id.
function subjectOf(r: Row): string {
  const subject = (r?.thesis_subject ?? "").toString().trim();
  if (subject) return subject;
  return beliefSentence(r?.from_label, r?.to_label, r?.thesis_direction);
}

/* ------------------------------------------------------------ Vs ourselves -- */
function VsOurselves() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useHouseViewConsistency(0, 200);

  if (isLoading) return <LoadingState rows={3} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;

  const rows: Row[] = Array.isArray(data) ? data : [];
  const tensions = rows.filter((r) => (r?.verdict ?? "").toLowerCase() !== "consistent");

  if (tensions.length === 0) {
    return <EmptyState title={EMPTY_COPY} tone="good" />;
  }

  return (
    <div className="space-y-2">
      {tensions.map((r, i) => {
        const v = verdict(r.verdict);
        const sentence = beliefSentence(r.from_label, r.to_label, r.thesis_direction);
        const hasEdge = !!r.edge_id;
        return (
          <Card
            key={r.edge_id ?? i}
            className={
              "p-3 flex items-center justify-between gap-3" +
              (hasEdge ? " cursor-pointer hover:bg-muted/50" : "")
            }
            onClick={() => hasEdge && navigate(`${HUB}/thesis/${r.edge_id}`)}
          >
            <div className="font-body text-sm truncate min-w-0">{sentence}</div>
            <div className="flex items-center gap-2 shrink-0">
              <AttentionBadge label={v.label} tone={v.tone} />
              <AskAboutThis
                prefill={`Tell me more about the tension in our view that ${sentence}.`}
                label=""
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
}
